import { useEffect, useRef, useState } from "react";
import Router, { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";

// redux
import { showAppModal } from "~/redux/actions/app";
import { signout } from "~/redux/actions/user";

// views
import AppHeaderSearch from "./AppHeaderSearch";
import Button from "~/components/common/Button";
import UserBadge from "~/components/user/UserBadge";

// context / hooks
import { useFeatures } from "~/context/FeaturesContext";
import useMyProjectsGuide from "~/hooks/useMyProjectsGuide";
import useProjectNavDot from "~/hooks/useProjectNavDot";

// utils
import settings from "~/settings";
import { avatarUrl, truncate } from "~/utils/app";
import { getUserBadge } from "~/utils/ui";
import { hasAnyRole, hasContactInfo } from "~/utils/user";
import { isWechatBrowser } from "~/utils/wechat";

// images
import duckIcon from "~/public/static/duck.svg";
import uprightLogo from "~/public/static/logo-upright.svg";

import styles from "./AppHeader.module.css";

// 站顶导航：广场 / 机会 / 项目 / 群组
const NAV_ITEMS = [
  { key: "home", label: "广场", href: "/" },
  { key: "jobs", label: "机会", href: "/jobs-channel" },
  // 「项目」是新增频道，红点在第一次进入项目广场后消失
  { key: "projects", label: "项目", href: "/projects", dot: true },
  { key: "groups", label: "群组", href: "/groups" },
];

// 当前导航项：沿用旧左侧菜单的匹配规则（/jobs_channel 相关的路径也算「招聘」）
const matchNavKey = (pathname) => {
  if (pathname === "/") return "home";
  if (pathname.includes("/jobs-channel") || pathname.includes("/jobs_channel"))
    return "jobs";
  if (pathname.startsWith("/projects")) return "projects";
  if (pathname.includes("/groups")) return "groups";
  return null;
};

const HeaderNav = ({ className, showProjectDot, onNavigate }) => {
  const router = useRouter();
  const activeKey = matchNavKey(router.pathname);

  return (
    <nav className={className}>
      {NAV_ITEMS.map((item) => (
        <a
          key={item.key}
          href={item.href}
          className={[
            styles.menuItem,
            activeKey === item.key ? styles.menuItemActive : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={onNavigate}
        >
          <span className={styles.menuLabel}>{item.label}</span>
          {item.dot && showProjectDot && activeKey !== item.key && (
            <i className={styles.navDot} />
          )}
        </a>
      ))}
    </nav>
  );
};

const HeaderUser = ({ open, onOpenChange, showProjectDot }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const messageCount = useSelector((state) => state.message.messageCount);
  const jobsSummary = useSelector((state) => state.app.jobsSummary);
  const features = useFeatures();
  const showMyProjectsGuide = useMyProjectsGuide(user);

  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  // 路由变化（含站内跳转）后收起面板
  useEffect(() => {
    const handleRouteChange = () => onOpenChange(false);
    Router.events.on("routeChangeComplete", handleRouteChange);
    return () => Router.events.off("routeChangeComplete", handleRouteChange);
  }, []);

  // 鼠标离开后延后收起：面板与触发区之间隔着 3px 下边框，留出鼠标穿过的时间
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openMenu = () => {
    clearTimeout(closeTimer.current);
    onOpenChange(true);
  };

  const closeMenuLater = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => onOpenChange(false), 150);
  };

  // 触屏上 tap 会先合成 mouseenter 再 click，两者都开合会产生「开了又关」，
  // 所以悬停只认 pointerType 为 mouse 的真实鼠标
  const canHover = (event) => event.pointerType === "mouse";

  // 点击面板外或按 Esc 收起
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) onOpenChange(false);
    };
    const handleEsc = (event) => event.key === "Escape" && onOpenChange(false);

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  const handleSignin = () =>
    dispatch(
      showAppModal(isWechatBrowser() ? "WechatSigninModal" : "PhoneSigninModal")
    );

  const handleSignup = () => dispatch(showAppModal("PhoneSignupModal"));

  if (!user) {
    return (
      <div className={styles.auth}>
        <Button
          size="sm"
          ghost
          onClick={handleSignin}
        >
          登录
        </Button>
        <Button
          size="sm"
          onClick={handleSignup}
        >
          注册
        </Button>
      </div>
    );
  }

  const hasUnreadApplicationMsg = messageCount.jobApplicationMsgCount > 0;
  const hasUnreadEmployerMsg = messageCount.employerMsgCount > 0;
  const hasPendingApplications = jobsSummary.pending_applications_count > 0;
  const isAdmin = hasAnyRole(user, ["admin"]);
  const isHr = hasAnyRole(user, ["hr"]);
  const hasAvatarDot =
    messageCount.total > 0 ||
    !hasContactInfo(user) ||
    hasPendingApplications ||
    showMyProjectsGuide;

  const menuItems = [
    {
      key: "profile",
      label: "个人主页",
      href: "/profile/info",
      dot: !hasContactInfo(user) || showMyProjectsGuide,
    },
    {
      key: "messages",
      label: "我的消息",
      href: "/messages",
      count: messageCount.total,
    },
    {
      key: "resume",
      label: "我的简历",
      href: `${settings.talentUrl}/me/profile`,
    },
    {
      key: "applications",
      label: "我的申请",
      href: "/applications/jobs_channel",
      dot: hasUnreadApplicationMsg,
    },
    {
      key: "recruitments",
      label: "招聘管理",
      href: "/recruitments?status=published",
      dot: hasPendingApplications || hasUnreadEmployerMsg,
    },
  ];

  if (features["group_manage"]) {
    menuItems.push({
      key: "groups-manage",
      label: "群组管理",
      href: "/groups/managed/",
    });
  }
  if (isHr) {
    menuItems.push({
      key: "talent-hr",
      label: "远程人才库-HR",
      href: `${settings.talentUrl}/hr`,
    });
  }
  if (isAdmin) {
    menuItems.push({
      key: "talent-admin",
      label: "远程人才库-管理员",
      href: `${settings.talentUrl}/admin`,
    });
    menuItems.push({
      key: "admin-reports",
      label: "后台管理-管理员",
      href: "/admin/reports",
    });
  }

  return (
    <div
      className={styles.me}
      ref={wrapRef}
      onPointerEnter={(event) => canHover(event) && openMenu()}
      onPointerLeave={(event) => canHover(event) && closeMenuLater()}
    >
      <span
        className={styles.meTrigger}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? onOpenChange(false) : openMenu())}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            open ? onOpenChange(false) : openMenu();
          }
        }}
      >
        <span className={styles.avaWrap}>
          <img
            className={styles.ava}
            style={getUserBadge({ user, avatarSize: 36 }).avatarBorderStyle}
            src={avatarUrl(user, 48)}
            alt={user.nickname}
          />
          {hasAvatarDot && <i className={styles.avaDot} />}
          <UserBadge
            user={user}
            position="AppMenuRight"
            avatarSize={36}
          />
        </span>
        <span className={styles.meName}>{truncate(user.nickname, 12)}</span>
      </span>

      {open && (
        <>
          <div
            className={styles.mask}
            onClick={() => onOpenChange(false)}
          />
          <div
            className={styles.panel}
            role="menu"
          >
            {/* 手机端导航收在面板里，桌面端隐藏 */}
            <div className={styles.panelNavSection}>
              <HeaderNav
                className={styles.panelNav}
                showProjectDot={showProjectDot}
                onNavigate={() => onOpenChange(false)}
              />
              <div className={styles.panelDivider} />
            </div>
            {menuItems.map((item) => (
              <a
                key={item.key}
                className={styles.panelItem}
                href={item.href}
                role="menuitem"
                onClick={() => onOpenChange(false)}
              >
                <span className={styles.panelLabel}>{item.label}</span>
                {item.count > 0 && (
                  <i className={styles.panelCount}>
                    {item.count > 99 ? "99+" : item.count}
                  </i>
                )}
                {item.dot && <i className={styles.panelDot} />}
              </a>
            ))}
            <div className={styles.panelDivider} />
            <a
              className={styles.panelItem}
              role="menuitem"
              onClick={() => {
                onOpenChange(false);
                dispatch(signout());
              }}
            >
              <span className={styles.panelLabel}>退出</span>
            </a>
          </div>
        </>
      )}
    </div>
  );
};

const AppHeader = () => {
  const showProjectDot = useProjectNavDot();
  // 同一时间只开一个下拉："search" | "me" | null
  const [openPanel, setOpenPanel] = useState(null);

  return (
    <header className={`app-header ${styles.header}`}>
      <div className="container">
        <div className={styles.inner}>
          <div className={styles.brand}>
            <a
              className={styles.logo}
              href="/"
              aria-label="电鸭"
            >
              <img
                src={duckIcon.src}
                width={27}
                height={26}
                alt=""
              />
              <img
                src={uprightLogo.src}
                width={50}
                height={23}
                alt="电鸭"
              />
            </a>
            <span className={styles.slogan}>找人·找机会</span>
          </div>
          <HeaderNav
            className={styles.menu}
            showProjectDot={showProjectDot}
          />
          <div className={styles.right}>
            <AppHeaderSearch
              open={openPanel === "search"}
              onOpenChange={(next) => setOpenPanel(next ? "search" : null)}
            />
            <HeaderUser
              open={openPanel === "me"}
              onOpenChange={(next) => setOpenPanel(next ? "me" : null)}
              showProjectDot={showProjectDot}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
